import { List, Avatar, Typography, Badge } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useConversations, IConversation } from '../hooks';
import styled from 'styled-components';

const { Text } = Typography;

const ListContainer = styled.div`
  height: 100%;
  max-height: 100%;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  
  /* Ocultar scrollbar en la lista */
  .ant-list {
    scrollbar-width: none;
    -ms-overflow-style: none;
    
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const ListHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid #e8e8e8;
  background: #fff;
  flex-shrink: 0;
`;

interface ConversationsListProps {
  selectedUserId?: number;
  onSelectUser: (userId: number, userName?: string) => void;
}

export const ConversationsList = ({ selectedUserId, onSelectUser }: ConversationsListProps) => {
  const { conversations, isLoading } = useConversations();

  const formatTime = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Ahora';
    if (minutes < 60) return `Hace ${minutes}m`;
    if (minutes < 1440) return `Hace ${Math.floor(minutes / 60)}h`;
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  return (
    <ListContainer>
      <ListHeader>
        <Text strong style={{ fontSize: 18 }}>
          Conversaciones
        </Text>
      </ListHeader>
      <List
        loading={isLoading}
        dataSource={conversations}
        style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
        renderItem={(conversation: IConversation) => (
          <List.Item
            style={{
              cursor: 'pointer',
              backgroundColor: selectedUserId === conversation.user_id ? '#e6f7ff' : 'transparent',
              padding: '12px 16px',
              borderBottom: '1px solid #f0f0f0',
            }}
            onClick={() => onSelectUser(
              conversation.user_id, 
              `${conversation.user?.first_name || ''} ${conversation.user?.last_name || ''}`.trim()
            )}
          >
            <List.Item.Meta
              avatar={
                <Badge count={conversation.unreadCount || 0} offset={[-5, 5]}>
                  <Avatar 
                    icon={<UserOutlined />} 
                    src={conversation.user?.email ? `https://ui-avatars.com/api/?name=${conversation.user.first_name}+${conversation.user.last_name}` : undefined}
                  />
                </Badge>
              }
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text strong>
                    {conversation.user?.first_name} {conversation.user?.last_name}
                  </Text>
                  {conversation.lastMessage && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {formatTime(conversation.lastMessage.createdAt)}
                    </Text>
                  )}
                </div>
              }
              description={
                conversation.lastMessage ? (
                  <Text 
                    ellipsis 
                    style={{ 
                      fontSize: 13,
                      color: conversation.unreadCount && conversation.unreadCount > 0 ? '#000' : '#8c8c8c',
                      fontWeight: conversation.unreadCount && conversation.unreadCount > 0 ? 500 : 'normal',
                    }}
                  >
                    {conversation.lastMessage.message}
                  </Text>
                ) : (
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    Sin mensajes
                  </Text>
                )
              }
            />
          </List.Item>
        )}
      />
      {!isLoading && conversations.length === 0 && (
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <Text type="secondary">No hay conversaciones</Text>
        </div>
      )}
    </ListContainer>
  );
};
